import React from "react";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { get, groupBy, includes, size, isEmpty } from "lodash";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card } from "react-bootstrap";
import { createErrorSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import { invalidBankIdsLinkedToGroupsSelector } from "store/selectors/bank";
import { FiList } from "react-icons/fi";
import type { RootState } from "types/state";

import Alert from "components/Alert";
import IconTable, { type IconTableColumn } from "components/IconTable";
import Button from "components/Button";
import IconMessageCard from "components/IconMessageCard";

import "./ProcessOneTimeContributions.scss";

const errorSelector = createErrorSelector(employerConstants.PROCESS_PAYROLL);

const mapStateToProps = (state: RootState) => {
  const linkedEmployees = state.employer.linkedEmployees;
  const linkedEmployeesByGroup = groupBy(
    linkedEmployees,
    "groupWithSchedule.group.id"
  );

  return {
    linkedEmployeesByGroup,
    groups: state.employer.groups,
    error: errorSelector(state),
    invalidBankIdsLinkedToGroups: invalidBankIdsLinkedToGroupsSelector(state),
  };
};

const mapDispatchToProps = {
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  showConfirmationModal: boolean;
  pendingCancellationId: string | null;
}

export class ProcessGroupsTable extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showConfirmationModal: false,
      pendingCancellationId: null,
    };
  }

  _buildColumns = (): Array<IconTableColumn<Record<string, unknown>>> => {
    const columns: Array<IconTableColumn<Record<string, unknown>>> = [
      {
        label: "Group Name",
        key: "[0]groupWithSchedule.group.name",
      },

      {
        label: "Number of Users",
        key: "length",
      },
      {
        label: "Action",
        customComponent: (props) => <>{this._getPayrollAction(props)}</>,
      },
    ];
    return columns;
  };

  _getPayrollAction = (linkedEmployees: unknown) => {
    const groupId = get(linkedEmployees, "[0]groupWithSchedule.group.id");

    const associatedBankAccountId = get(
      linkedEmployees,
      "[0]groupWithSchedule.group.associatedBankAccount.id"
    );

    const shouldUpdateBank = includes(
      this.props.invalidBankIdsLinkedToGroups,
      associatedBankAccountId
    );

    if (shouldUpdateBank) {
      return (
        <Button
          color="action"
          name="action"
          size="sm"
          btnLabel="Update Bank"
          onClick={() => {
            this.props.push("/dashboard/company/group");
          }}
        />
      );
    }
    return (
      <Button
        color="action"
        name="action"
        size="sm"
        btnLabel="Review Payroll"
        onClick={() =>
          this.props.push(
            `/dashboard/contributions/${String(groupId)}/one-time`
          )
        }
      />
    );
  };

  render() {
    const columns = this._buildColumns();
    const numberOfGroups = size(this.props.linkedEmployeesByGroup);
    const payrollEmpty = isEmpty(this.props.groups);
    return (
      <Card className="one-time-contributions">
        <div className="widget-header">
          <Card.Title>
            Payroll groups:
            <p className="invite-number">
              Groups (<p className="number">{numberOfGroups}</p>)
            </p>
          </Card.Title>
        </div>
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        {!payrollEmpty && (
          <IconTable
            columns={columns}
            // linkedEmployeesByGroup is a lodash groupBy Dictionary (object),
            // not an array; IconTable iterates it with lodash map at runtime
            // (works on objects). Cast preserves the original runtime behavior.
            data={
              this.props.linkedEmployeesByGroup as unknown as Array<
                Record<string, unknown>
              >
            }
          />
        )}
        {payrollEmpty && (
          <div style={{ borderTop: "1px solid #f2f4f7" }}>
            <IconMessageCard
              header={"No payrolls to process"}
              subheader="Please add a group and employees in order to process a payroll."
              icon={<FiList color="white" stroke="#60A4BF" size={16} />}
            />
          </div>
        )}
      </Card>
    );
  }
}

export default connector(withApollo<Props>(ProcessGroupsTable));
